import { API_BASE_URL } from '@synergy/config';
import { Box, Button } from '@mui/material';
import { DownloadIcon } from '@synergy/theme-next';
import { Filter, SearchFilter } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { PaloDetailView, PaloSideRail, PaloStatusPill } from '@synergy/components-palo';
import { ReactElement, useEffect } from 'react';
import { Route, Routes, useSearchParams } from 'react-router-dom';
import { getBonusSettlementTranslationId } from '@synergy/models';
import { noop } from '@synergy/utils';
import { useNavigate, useParams } from 'react-router';
import urlJoin from 'url-join';

import { BonusSettlementDetailTable } from '../../components/bonus-settlement-detail-table';
import { BonusSettlementTab } from '../../../models/bonus-settlement-tab';
import { PaloBonusSettlementDetailDock } from '../../palo/palo-bonus-settlement-detail-dock';
import { ROUTES } from '../../../../sidebar-menu/navigation-drawer/routes';
import {
    bonusSettlementPoolDetailSidebarSelector,
    bonusSettlementPoolDetailSuppliersSelector,
} from '../store/bonus-settlements-pool-detail-selectors';
import {
    bonusSettlementPoolDetailSuppliersReset,
    bonusSettlementPoolDetailsReset,
    getPoolBonusSettlementCustomerDetails,
    getPoolBonusSettlementCustomerSuppliers,
} from '../store/bonus-settlements-pool-detail-actions';
import { useAppDispatch, useAppSelector } from '../../../../../store/store';
import { useSelector } from '../../../../../store/use-appstate-selector';

export const PaloBonusSettlementPoolDetail = (): ReactElement | null => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const currency = useSelector(state => state.currency);
    const [searchParams, setSearchParams] = useSearchParams();

    const { id: bonusSettlementId, customerBonusSettlementId } = useParams<{
        id: string;
        customerBonusSettlementId: string;
    }>();

    const { poolBonusSettlementCustomerDetails } = useAppSelector(bonusSettlementPoolDetailSidebarSelector);
    const { poolBonusSettlementCustomerSuppliers } = useAppSelector(bonusSettlementPoolDetailSuppliersSelector);

    useEffect(
        () => () => {
            dispatch(bonusSettlementPoolDetailsReset());
            dispatch(bonusSettlementPoolDetailSuppliersReset());
        },
        [bonusSettlementId, dispatch]
    );

    useEffect(() => {
        if (
            bonusSettlementId &&
            !isNaN(+bonusSettlementId) &&
            customerBonusSettlementId &&
            !isNaN(+customerBonusSettlementId)
        ) {
            const args = {
                id: +bonusSettlementId,
                customerId: +customerBonusSettlementId,
                searchParams,
            };

            dispatch(getPoolBonusSettlementCustomerSuppliers(args));
            dispatch(getPoolBonusSettlementCustomerDetails(args));
        }
    }, [bonusSettlementId, customerBonusSettlementId, searchParams, dispatch]);

    if (!poolBonusSettlementCustomerDetails) {
        return null;
    }

    return (
        <PaloDetailView
            onBack={() =>
                navigate(
                    bonusSettlementId
                        ? ROUTES.bonusSettlements.detail.pool.replace(':id', bonusSettlementId)
                        : ROUTES.bonusSettlements.detail.base
                )
            }
            title={`${poolBonusSettlementCustomerDetails.customerName} (${poolBonusSettlementCustomerDetails.year})`}
            status={
                <PaloStatusPill
                    status={poolBonusSettlementCustomerDetails.status}
                    label={
                        <FormattedMessage
                            id={getBonusSettlementTranslationId(poolBonusSettlementCustomerDetails.status)}
                        />
                    }
                />
            }
            actions={
                <Button
                    variant="outlined"
                    color="primary"
                    endIcon={<DownloadIcon />}
                    onClick={() =>
                        window.open(urlJoin(API_BASE_URL, `/bonus-settlements/${bonusSettlementId}/export`), '_self')
                    }
                >
                    <FormattedMessage id="app.common.export" />
                </Button>
            }
            tabs={
                <Box sx={{ paddingBottom: '4px' }}>
                    <Filter searchParams={searchParams} onUpdateSearchParams={setSearchParams}>
                        <SearchFilter />
                    </Filter>
                </Box>
            }
            dock={
                <PaloSideRail
                    sections={[
                        {
                            key: 'details',
                            label: <FormattedMessage id="app.common.details" />,
                            content: (
                                <PaloBonusSettlementDetailDock
                                    currency={currency}
                                    bonusSettlement={poolBonusSettlementCustomerDetails}
                                />
                            ),
                        },
                    ]}
                    collapsedLabel={<FormattedMessage id="app.common.details" />}
                />
            }
        >
            <Box sx={{ height: '100%', overflowY: 'auto' }}>
                <Routes>
                    <Route
                        path={BonusSettlementTab.Customer}
                        element={
                            <BonusSettlementDetailTable
                                currency={currency}
                                requesting={!!poolBonusSettlementCustomerSuppliers}
                                onItemSelected={noop}
                                data={poolBonusSettlementCustomerSuppliers}
                            />
                        }
                    />
                </Routes>
            </Box>
        </PaloDetailView>
    );
};
